// dj-plp.js — behavior for the DJ collection-page sections (dj-collection-4a, the default
// collection layout, and the alternative dj-collection-4b).
//
// Three responsibilities, all via event delegation so section re-renders keep working:
//   1. [data-dj-qa-toggle] — expand/collapse a card's inline size-pill panel (mockup interaction).
//   2. [data-dj-qa-add]    — add a variant to cart via /cart/add.js, then dispatch the theme's
//      'cart:update' event shaped exactly like Horizon's CartAddEvent (see assets/events.js):
//      cart-icon.js increments the bubble when data.source === 'product-form-component' and
//      data.itemCount is the DELTA; cart-drawer.js auto-opens and announces resource.item_count;
//      cart-items-component re-renders from data.sections (we request them in the POST).
//   3. [data-dj-sort]      — sort <select> navigates, preserving active filter params.
//
// Self-contained on purpose: it does not import the theme's Component framework, only speaks
// its documented event contract, so theme updates can't silently break the sections.
(function () {
  'use strict';
  if (window.__djPlpInit) return;
  window.__djPlpInit = true;

  var busy = false;

  // Cart routes come from the host section (data-cart-add-url / data-cart-url, rendered from
  // Liquid `routes`, so locale-prefixed shops work). Horizon's own `Theme.routes.cart_add_url`
  // already carries a `.js` suffix and `Theme` is a module-scoped const (not on window), so we
  // never rely on it; whatever we get is normalised to exactly one trailing `.js`.
  function jsRoute(base, fallback) {
    var url = (base || fallback).replace(/\/+$/, '');
    return /\.js$/.test(url) ? url : url + '.js';
  }

  function routesFor(el) {
    var host = el && el.closest ? el.closest('[data-dj-plp]') : null;
    var ds = (host && host.dataset) || {};
    return {
      add: jsRoute(ds.cartAddUrl, '/cart/add'),
      cart: jsRoute(ds.cartUrl, '/cart'),
    };
  }

  function sectionIdsForCartItems() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids.join(',');
  }

  function flash(btn, text, cls, revertMs) {
    var original = btn.dataset.djLabel || btn.textContent;
    btn.dataset.djLabel = original;
    btn.textContent = text;
    btn.classList.add(cls);
    setTimeout(function () {
      btn.textContent = btn.dataset.djLabel;
      btn.classList.remove(cls);
    }, revertMs);
  }

  function announce(card, message) {
    var region = card.closest('[data-dj-plp]');
    region = region && region.querySelector('[data-dj-live]');
    if (region) region.textContent = message;
  }

  function addToCart(btn) {
    if (busy) return;
    busy = true;
    btn.disabled = true;
    btn.classList.add('dj-plp-is-loading');

    var variantId = btn.dataset.variantId;
    var qty = parseInt(btn.dataset.qty || '1', 10) || 1;
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', String(qty));
    var sections = sectionIdsForCartItems();
    if (sections) body.append('sections', sections);
    var routes = routesFor(btn);

    fetch(routes.add, { method: 'POST', headers: { Accept: 'application/json' }, body: body })
      .then(function (res) { return res.json(); })
      .then(function (added) {
        if (added && added.status) throw new Error(added.description || added.message || 'Could not add to cart');
        return fetch(routes.cart, { headers: { Accept: 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: btn.id || 'dj-plp-' + variantId,
                  data: {
                    source: 'product-form-component',
                    itemCount: qty,
                    productId: btn.dataset.productId,
                    variantId: variantId,
                    sections: added && added.sections ? added.sections : undefined,
                  },
                },
              })
            );
            var card = btn.closest('[data-dj-plp-card]');
            if (card) announce(card, 'Added to cart');
            flash(btn, '✓', 'dj-plp-is-added', 1200);
            // Collapse the pill panel shortly after a successful add
            if (card) {
              var toggle = card.querySelector('[data-dj-qa-toggle][aria-expanded="true"]');
              var panel = card.querySelector('.dj-plp-card__panel');
              if (toggle && panel) {
                setTimeout(function () {
                  panel.hidden = true;
                  toggle.setAttribute('aria-expanded', 'false');
                }, 900);
              }
            }
          });
      })
      .catch(function (err) {
        flash(btn, 'Error', 'dj-plp-is-error', 1600);
        var card = btn.closest('[data-dj-plp-card]');
        if (card) announce(card, 'Could not add to cart. ' + (err && err.message ? err.message : ''));
      })
      .then(function () {
        busy = false;
        btn.disabled = false;
        btn.classList.remove('dj-plp-is-loading');
      });
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-dj-qa-toggle]');
    if (toggle) {
      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      var open = toggle.getAttribute('aria-expanded') === 'true';
      // Close any other open panels within the same grid first
      var grid = toggle.closest('[data-dj-plp]');
      if (grid && !open) {
        grid.querySelectorAll('[data-dj-qa-toggle][aria-expanded="true"]').forEach(function (other) {
          var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
          if (otherPanel) otherPanel.hidden = true;
          other.setAttribute('aria-expanded', 'false');
        });
      }
      panel.hidden = open;
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      return;
    }

    var add = e.target.closest('[data-dj-qa-add]');
    if (add) addToCart(add);
  });

  document.addEventListener('change', function (e) {
    var sort = e.target.closest('[data-dj-sort]');
    if (!sort) return;
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  });
})();
